import React, { useState, useEffect } from 'react';
import { FiTrash2, FiCopy } from 'react-icons/fi';

const getSamplePayloadHint = (event) => {
    const hints = {
        'EVENT': 'e.g., data.receiptNumber, data.customer.firstName',
        'INBOUND_WEBHOOK': 'e.g., trigger.body.some_field, trigger.headers.authorization',
    };
    return hints[event] || 'e.g., data.someProperty';
};

const WorkflowForm = ({ workflow, events, onSave, onCancel, error }) => {
  const [formData, setFormData] = useState({
    name: '',
    triggerType: 'EVENT',
    event: events[0] || '',
    actionType: 'CALL_API',
    config: {
      url: '',
      method: 'POST',
      headers: [],
      bodyMapping: [],
      entity: 'Customer',
      findField: '_id',
      findValueSource: '',
      updates: [],
    },
    isActive: true,
  });

  useEffect(() => {
    if (workflow) setFormData(workflow);
  }, [workflow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, config: { ...prev.config, [name]: value } }));
  };

  const handleArrayChange = (arrayName, index, field, value, isCheckbox = false) => {
    const newArray = [...formData.config[arrayName]];
    newArray[index][field] = isCheckbox ? !newArray[index][field] : value;
    setFormData(prev => ({ ...prev, config: { ...prev.config, [arrayName]: newArray } }));
  };

  const addArrayItem = (arrayName, newItem) => {
    setFormData(prev => ({ ...prev, config: { ...prev.config, [arrayName]: [...prev.config[arrayName], newItem] } }));
  };

  const removeArrayItem = (arrayName, index) => {
    const newArray = formData.config[arrayName].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, config: { ...prev.config, [arrayName]: newArray } }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('URL copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto space-y-4">
        <h2 className="text-2xl font-bold mb-4">{workflow ? 'Edit Workflow' : 'Create New Workflow'}</h2>
        {error && <p className="bg-red-100 text-red-700 p-3 rounded">{error}</p>}
        
        <input name="name" value={formData.name} onChange={handleChange} placeholder="Workflow Name (e.g., Send SMS on Sale)" required className="p-2 border rounded-md w-full" />
        
        <div>
          <label className="block text-sm font-medium text-gray-700">When this happens... (Trigger)</label>
          <select name="triggerType" value={formData.triggerType} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full">
            <option value="EVENT">An event happens in this POS</option>
            <option value="INBOUND_WEBHOOK">An external service calls a URL</option>
          </select>
          {formData.triggerType === 'EVENT' && (
            <select name="event" value={formData.event} onChange={handleChange} className="mt-2 p-2 border rounded-md w-full">
              {events.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
          {formData.triggerType === 'INBOUND_WEBHOOK' && formData.ingressUrl && (
             <div className="mt-2 bg-gray-100 p-2 rounded-md flex items-center justify-between font-mono text-sm">
                <span>{formData.ingressUrl}</span>
                <button type="button" onClick={() => copyToClipboard(formData.ingressUrl)} className="text-gray-600 hover:text-blue-600"><FiCopy /></button>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <h3 className="font-semibold">Do this... (Action)</h3>
          <select name="actionType" value={formData.actionType} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full">
            <option value="CALL_API">Call an External API</option>
            <option value="INTERNAL_POS_ACTION">Perform an Action in this POS</option>
          </select>
        </div>

        {formData.actionType === 'CALL_API' && (
          <div className="p-3 border rounded-md mt-2 space-y-3">
            <div className="flex gap-2">
              <select name="method" value={formData.config.method} onChange={handleConfigChange} className="p-2 border rounded-md">
                <option>POST</option><option>GET</option><option>PUT</option><option>PATCH</option>
              </select>
              <input name="url" value={formData.config.url} onChange={handleConfigChange} placeholder="https://api.externalservice.com/endpoint" required className="p-2 border rounded-md w-full" />
            </div>
            <h4 className="font-semibold text-sm">Headers</h4>
            {formData.config.headers.map((header, index) => (
              <div key={index} className="flex items-center gap-2">
                <input value={header.key} onChange={(e) => handleArrayChange('headers', index, 'key', e.target.value)} placeholder="Header Name" className="p-2 border rounded-md w-1/3" />
                <input value={header.value} onChange={(e) => handleArrayChange('headers', index, 'value', e.target.value)} placeholder="Header Value" className="p-2 border rounded-md flex-grow" />
                <label className="text-sm flex items-center"><input type="checkbox" checked={header.isSecret} onChange={() => handleArrayChange('headers', index, 'isSecret', null, true)} className="mr-1" /> Secret</label>
                <button type="button" onClick={() => removeArrayItem('headers', index)} className="text-red-500 p-2"><FiTrash2 /></button>
              </div>
            ))}
            <button type="button" onClick={() => addArrayItem('headers', { key: '', value: '', isSecret: false })} className="text-sm text-blue-600">Add Header</button>
            <h4 className="font-semibold text-sm pt-2">Body Mapping</h4>
            <p className="text-xs text-gray-500">Hint: {getSamplePayloadHint(formData.triggerType)}</p>
            {formData.config.bodyMapping.map((map, index) => (
              <div key={index} className="flex items-center gap-2">
                <input value={map.target} onChange={(e) => handleArrayChange('bodyMapping', index, 'target', e.target.value)} placeholder="Target Key" className="p-2 border rounded-md w-1/3" />
                <input value={map.source} onChange={(e) => handleArrayChange('bodyMapping', index, 'source', e.target.value)} placeholder="Source Path or Template" className="p-2 border rounded-md flex-grow" />
                <button type="button" onClick={() => removeArrayItem('bodyMapping', index)} className="text-red-500 p-2"><FiTrash2 /></button>
              </div>
            ))}
            <button type="button" onClick={() => addArrayItem('bodyMapping', { target: '', source: '' })} className="text-sm text-blue-600">Add Mapping</button>
          </div>
        )}

        {formData.actionType === 'INTERNAL_POS_ACTION' && (
            <div className="p-3 border rounded-md mt-2 space-y-3">
                <label className="block text-sm font-medium">Find a record in...</label>
                <select name="entity" value={formData.config.entity} onChange={handleConfigChange} className="p-2 border rounded-md w-full">
                    <option value="Customer">Customer</option>
                    <option value="Product">Product</option>
                    <option value="Sale">Sale</option>
                </select>
                <div className="flex gap-2">
                    <input name="findField" value={formData.config.findField} onChange={handleConfigChange} placeholder="Field Name (e.g., _id)" className="p-2 border rounded-md w-1/2" />
                    <input name="findValueSource" value={formData.config.findValueSource} onChange={handleConfigChange} placeholder="Value from Trigger (e.g., {{trigger.body.id}})" className="p-2 border rounded-md w-1/2" />
                </div>
                <h4 className="font-semibold text-sm pt-2">And update these fields...</h4>
                {formData.config.updates.map((upd, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <input value={upd.field} onChange={(e) => handleArrayChange('updates', index, 'field', e.target.value)} placeholder="Field to Update (e.g., notes)" className="p-2 border rounded-md w-1/3" />
                        <input value={upd.value} onChange={(e) => handleArrayChange('updates', index, 'value', e.target.value)} placeholder="New Value or Template" className="p-2 border rounded-md flex-grow" />
                        <button type="button" onClick={() => removeArrayItem('updates', index)} className="text-red-500 p-2"><FiTrash2 /></button>
                    </div>
                ))}
                <button type="button" onClick={() => addArrayItem('updates', { field: '', value: '' })} className="text-sm text-blue-600">Add Update Action</button>
            </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
          <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md">Cancel</button>
          <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Save Workflow</button>
        </div>
      </form>
    </div>
  );
};

export default WorkflowForm;
